/**
 * Smooths the in-place product gallery swap on variant changes by fading
 * <media-gallery> out on `variant:selected` (before the morph) and back in on
 * `variant:update` (after the morph). A safety timeout removes the class even
 * if `variant:update` never fires.
 *
 * Combined-listing product swaps are instead crossfaded by variant-picker via
 * the View Transitions API; when that path will run we skip the opacity dim so
 * the two effects don't stack. The condition mirrors startViewTransition's own
 * bail-out (unsupported / low-power / reduced motion → fall back to the dim).
 */
import { ThemeEvents } from '@theme/events';
import { supportsViewTransitions, isLowPowerDevice, prefersReducedMotion } from '@theme/utilities';

const SWAP_CLASS = 'media-gallery--swapping';
const FALLBACK_RESTORE_MS = 600;

let pendingRestore = null;

/**
 * @param {Event} [event] - The variant:selected event.
 */
function markSwapping(event) {
  const willCrossfade =
    Boolean(/** @type {CustomEvent} */ (event)?.detail?.loadsNewProduct) &&
    supportsViewTransitions() &&
    !isLowPowerDevice() &&
    !prefersReducedMotion();

  if (willCrossfade) return;

  document.querySelectorAll('media-gallery').forEach((el) => {
    el.classList.add(SWAP_CLASS);
  });
  document.body.classList.add(SWAP_CLASS);

  if (pendingRestore) clearTimeout(pendingRestore);
  pendingRestore = window.setTimeout(restore, FALLBACK_RESTORE_MS);
}

function restore() {
  if (pendingRestore) {
    clearTimeout(pendingRestore);
    pendingRestore = null;
  }
  // Wait two frames so the new gallery DOM is committed before we fade back.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.querySelectorAll('media-gallery').forEach((el) => {
        el.classList.remove(SWAP_CLASS);
      });
      document.body.classList.remove(SWAP_CLASS);
    });
  });
}

document.addEventListener(ThemeEvents.variantSelected, markSwapping);
document.addEventListener(ThemeEvents.variantUpdate, restore);
